import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Spin, Badge, Modal, Form, Select, InputNumber, Carousel, message } from 'antd';
import {
  SwapOutlined, FileAddOutlined, DeleteOutlined,
  AuditOutlined, SettingOutlined, UndoOutlined,
  SearchOutlined, FilterOutlined, BellOutlined, UserOutlined,
  CheckSquareOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import api from '../api';
import {
  canApproveAny,
  getPermKeys,
  getStoredUser,
  hasPerm,
  isRegionManager,
  isSuperAdmin,
} from '../userAuth';

const actionColors = {
  '归还': '#52c41a',
  '申请借用': '#1677FF',
  '申请变更': '#722ed1',
  '申请处置': '#fa8c16',
  '申请审核': '#722ed1',
};

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchVisible, setSearchVisible] = useState(false);
  const [userInfoVisible, setUserInfoVisible] = useState(false);
  const [unread, setUnread] = useState(0);
  const [form] = Form.useForm();
  const [locations, setLocations] = useState([]);
  const [assetUsers, setAssetUsers] = useState([]);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);
  const user = getStoredUser();
  const permKeys = getPermKeys(user);
  const canApprove = canApproveAny(user);
  const canApplyScrap = hasPerm(user, 'apply_scrap');
  const canPublishAnnouncement = hasPerm(user, 'publish_announcement');

  useEffect(() => {
    api.get('/dashboard/stats').then(res => setStats(res)).catch(() => {}).finally(() => setLoading(false));
    api.get('/messages/unread-count').then(res => setUnread(res?.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!searchVisible) return undefined;
    let cancelled = false;
    setFilterOptsLoading(true);
    api
      .get('/assets/filter-options')
      .then((data) => {
        if (cancelled) return;
        setLocations(Array.isArray(data?.locations) ? data.locations : []);
        setAssetUsers(Array.isArray(data?.asset_users) ? data.asset_users : []);
      })
      .catch((err) => {
        if (!cancelled) {
          message.error(err.message || '加载筛选项失败，请稍后重试');
          setLocations([]);
          setAssetUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFilterOptsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchVisible]);

  const formatValue = useCallback((val) => {
    if (val == null) return '-';
    if (val >= 10000) return (val / 10000).toFixed(1) + '万';
    return val.toLocaleString();
  }, []);

  const handleSearch = (keyword) => {
    if (keyword) navigate(`/assets?keyword=${encodeURIComponent(keyword)}`);
  };

  const handleAdvancedSearch = () => {
    const values = form.getFieldsValue();
    const params = new URLSearchParams();
    const map = {
      asset_name: values.name,
      asset_code: values.asset_no,
      check_code: values.check_no,
      location_id: values.location_id,
      user_id: values.user_id,
      asset_status: values.asset_status,
      net_value_min: values.net_value_min,
      net_value_max: values.net_value_max,
      remaining_months_min: values.remaining_months_min,
      remaining_months_max: values.remaining_months_max,
    };
    Object.entries(map).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    if (qs) navigate(`/assets?${qs}`);
    setSearchVisible(false);
  };

  const clampPercent = (p) => Math.max(0, Math.min(100, Number.isFinite(p) ? p : 0));
  const calcPercent = (net, original) => {
    const o = Number(original || 0);
    const n = Number(net || 0);
    if (o <= 0) return 0;
    return clampPercent(Math.round((n / o) * 100));
  };

  const Donut = ({ percent, title }) => {
    const p = clampPercent(percent);
    const size = 60;
    const stroke = 9;
    const bg = `conic-gradient(#52c41a 0 ${p}%, #f0f0f0 ${p}% 100%)`;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            background: bg,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: stroke,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              lineHeight: 1.1,
            }}
          >
            <div style={{ fontSize: 14, color: '#52c41a', fontWeight: 800 }}>{p}%</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#666' }}>{title}</div>
      </div>
    );
  };

  const StatSlide = ({
    countLabel,
    countValue,
    originalLabel,
    originalValue,
    netLabel,
    netValue,
    percentTitle,
    percent,
  }) => (
    <div style={{
      background: 'linear-gradient(135deg, #5DB6FF 0%, #A7DCFF 100%)',
      borderRadius: 12,
      padding: 14,
      minHeight: 148,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 12,
        alignItems: 'stretch',
      }}
      >
        <div style={{ gridColumn: 1, gridRow: 1 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>{countLabel}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{countValue ?? 0}</div>
        </div>
        <div style={{
          gridColumn: 2,
          gridRow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <Donut percent={percent} title={percentTitle} />
          </div>
        </div>
        <div style={{ gridColumn: 1, gridRow: 2, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>{originalLabel}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>
            ¥{formatValue(originalValue || 0)}
          </div>
        </div>
        <div style={{ gridColumn: 2, gridRow: 2, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginBottom: 6, textAlign: 'center' }}>{netLabel}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', textAlign: 'center' }}>
            ¥{formatValue(netValue || 0)}
          </div>
        </div>
      </div>
    </div>
  );

  const actions = [
    { label: '归还', icon: <UndoOutlined />, color: actionColors['归还'], path: '/return' },
    { label: '申请借用', icon: <FileAddOutlined />, color: actionColors['申请借用'], path: '/apply-borrow' },
    { label: '申请变更', icon: <SwapOutlined />, color: actionColors['申请变更'], path: '/assets' },
  ];
  if (canApplyScrap && !isSuperAdmin(user)) {
    actions.push({
      icon: <DeleteOutlined />,
      label: '申请处置',
      color: actionColors['申请处置'],
      path: '/assets?asset_status=1&net_value_min=0&net_value_max=0&scrap_apply_only=1',
    });
    actions.push({
      label: '已处置资产',
      icon: <DeleteOutlined />,
      color: '#ff4d4f',
      path: '/assets?disposed_only=1',
    });
  }
  if (canApprove) {
    actions.push({
      label: '申请审核',
      icon: <AuditOutlined />,
      color: actionColors['申请审核'],
      path: '/approve',
      badge: stats?.pending_approval,
    });
  }
  if (isSuperAdmin(user) || isRegionManager(user)) {
    actions.push({
      label: '资产盘点',
      icon: <CheckSquareOutlined />,
      color: '#13c2c2',
      path: '/inventory',
    });
  }
  if (canPublishAnnouncement && !isSuperAdmin(user)) {
    actions.push({
      label: '公告管理',
      icon: <BellOutlined />,
      color: '#1677FF',
      path: '/admin/announcements',
    });
  }
  if (isSuperAdmin(user)) {
    actions.push({
      label: '处置资产',
      icon: <DeleteOutlined />,
      color: '#fa8c16',
      path: '/disposal',
    });
    actions.push({
      label: '操作日志',
      icon: <FileSearchOutlined />,
      color: '#722ed1',
      path: '/admin/op-logs',
    });
  }

  const unitPercent = stats ? calcPercent(stats.total_net_value, stats.total_original_value) : 0;
  const myPercent = stats ? calcPercent(stats.my_total_net_value, stats.my_total_original_value) : 0;
  const username = String(user?.username || '').trim();
  const usernameChars = Array.from(username);
  const avatarText = usernameChars.length > 0 ? usernameChars[usernameChars.length - 1] : '';
  const workNo = String(user?.work_no || '').trim();

  return (
    <div style={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{
        background: 'linear-gradient(180deg, #1677FF 0%, #4096ff 100%)',
        borderRadius: '0 0 12px 12px',
        padding: '12px 16px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', minHeight: 42 }}>
          <div style={{
            width: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexShrink: 0,
          }}>
            <div
              onClick={() => setUserInfoVisible(true)}
              style={{
                width: 40, height: 40, borderRadius: 20,
                background: 'rgba(255,255,255,0.3)', border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 18, fontWeight: 600, flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              {avatarText || <UserOutlined style={{ fontSize: 16 }} />}
            </div>
          </div>
          <div style={{
            flex: 1,
            textAlign: 'center',
            color: '#fff',
            fontSize: 18,
            fontWeight: 700,
            padding: '0 8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          >
            云仓智管
          </div>
          <div style={{ width: 52, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <Badge count={unread} size="small" offset={[-2, 2]}>
              <BellOutlined
                style={{ fontSize: 22, color: '#fff', cursor: 'pointer' }}
                onClick={() => navigate('/messages')}
              />
            </Badge>
          </div>
        </div>
      </div>

      <div style={{ margin: '8px 16px 0' }}>
        <div style={{
          display: 'flex',
          gap: 8,
          background: '#fff',
          borderRadius: 10,
          padding: 6,
          border: '1px solid #e8e8e8',
        }}
        >
          <Input
            placeholder="搜索资产名称/清查编号/资产编号"
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            style={{ flex: 1, borderRadius: 20, height: 34, background: '#fff' }}
            onPressEnter={e => handleSearch(e.target.value)}
          />
          <div
            onClick={() => setSearchVisible(true)}
            style={{
              width: 34, height: 34, borderRadius: 17,
              background: '#E8F4FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <FilterOutlined style={{ color: '#1677FF', fontSize: 16 }} />
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        <div style={{ margin: '12px 16px' }}>
          <Carousel dots autoplay autoplaySpeed={5500} effect="fade" speed={650}>
            <div>
              <StatSlide
                countLabel="单位资产总数（个）"
                countValue={stats?.total_assets ?? 0}
                originalLabel="单位资产总原值"
                originalValue={stats?.total_original_value ?? 0}
                netLabel="单位资产总净值"
                netValue={stats?.total_net_value ?? 0}
                percentTitle="净值/原值"
                percent={unitPercent}
              />
            </div>
            <div>
              <StatSlide
                countLabel="我的资产总数（个）"
                countValue={stats?.my_assets ?? 0}
                originalLabel="我的资产总原值"
                originalValue={stats?.my_total_original_value ?? 0}
                netLabel="我的资产总净值"
                netValue={stats?.my_total_net_value ?? 0}
                percentTitle="净值/原值"
                percent={myPercent}
              />
            </div>
          </Carousel>
        </div>
      </Spin>

      <div style={{ padding: '0 16px' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>常用功能</div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
        }}>
          {actions.map(a => (
            <div
              key={a.label}
              onClick={() => navigate(a.path)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            >
              <Badge count={a.badge || 0} size="small" offset={[-4, 4]}>
                <div style={{
                  width: 56, height: 56, borderRadius: 28,
                  background: a.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {React.cloneElement(a.icon, { style: { fontSize: 24, color: '#fff' } })}
                </div>
              </Badge>
              <span style={{ fontSize: 12, color: '#333', marginTop: 8 }}>{a.label}</span>
            </div>
          ))}

          {isSuperAdmin(user) && (
            <div
              onClick={() => navigate('/admin')}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: 28,
                background: '#f0f0f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SettingOutlined style={{ fontSize: 24, color: '#666' }} />
              </div>
              <span style={{ fontSize: 12, color: '#333', marginTop: 8 }}>云仓智管后台</span>
            </div>
          )}
        </div>
      </div>

      {userInfoVisible && (
        <div
          onClick={() => setUserInfoVisible(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 58,
              left: 12,
              minWidth: 150,
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
              border: '1px solid #f0f0f0',
              padding: '10px 12px',
              lineHeight: 1.8,
              fontSize: 13,
              color: '#333',
            }}
          >
            <div>姓名：{username || '-'}</div>
            <div>工号：{workNo || '-'}</div>
          </div>
        </div>
      )}

      <Modal
        title="高级查询"
        open={searchVisible}
        onCancel={() => setSearchVisible(false)}
        onOk={handleAdvancedSearch}
        okText="查询"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: '#999', marginBottom: 12, lineHeight: 1.5 }}>
          「使用人」与「用户管理」为同一张用户表；下拉仅显示姓名。存放地点来自「存放地点管理」。若为空请先在后台维护后再筛选。
        </div>
        <Form form={form} layout="vertical">
          <Form.Item label="资产名称" name="name">
            <Input placeholder="请输入资产名称" allowClear />
          </Form.Item>
          <Form.Item label="资产编号" name="asset_no">
            <Input placeholder="请输入资产编号" allowClear />
          </Form.Item>
          <Form.Item label="清查编号" name="check_no">
            <Input placeholder="请输入清查编号" allowClear />
          </Form.Item>
          <Form.Item label="存放位置" name="location_id">
            <Select
              placeholder={filterOptsLoading ? '加载中…' : '请选择存放位置'}
              allowClear
              showSearch
              loading={filterOptsLoading}
              optionFilterProp="label"
              notFoundContent={filterOptsLoading ? '加载中…' : '暂无地点，请在后台添加存放地点'}
              options={locations.map(l => ({
                value: Number(l.id),
                label: `${l.location_name || ''}${l.region_name ? `（${l.region_name}）` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item label="使用人" name="user_id">
            <Select
              placeholder={filterOptsLoading ? '加载中…' : '请选择使用人'}
              allowClear
              showSearch
              loading={filterOptsLoading}
              optionFilterProp="label"
              notFoundContent={filterOptsLoading ? '加载中…' : '暂无用户，请在「用户管理」中添加'}
              options={assetUsers.map(u => ({
                value: Number(u.id),
                label: u.username || '',
              }))}
            />
          </Form.Item>
          <Form.Item label="资产状态" name="asset_status">
            <Select
              placeholder="请选择资产状态"
              allowClear
              options={[
                { value: 0, label: '折旧中' },
                { value: 1, label: '已完成折旧' },
              ]}
            />
          </Form.Item>
          <Form.Item label="净值区间">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Form.Item name="net_value_min" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最小值" />
              </Form.Item>
              <span>-</span>
              <Form.Item name="net_value_max" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最大值" />
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item label="剩余折旧月数">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Form.Item name="remaining_months_min" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最小值" min={0} />
              </Form.Item>
              <span>-</span>
              <Form.Item name="remaining_months_max" noStyle>
                <InputNumber style={{ flex: 1 }} placeholder="最大值" min={0} />
              </Form.Item>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
